import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, LogOut, RefreshCw } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LuxuryButton } from "@/components/LuxuryButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ayurenew_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (tok) => {
    try {
      const { data } = await axios.get(`${API}/enquiries`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setEnquiries(data);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setEnquiries(null);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (e2) {
      setError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries(null);
    setEmail("");
    setPassword("");
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Logo />
          <h1 className="mt-10 font-serif text-3xl font-light sm:text-4xl">
            Admin <em className="text-accent">sign in</em>
          </h1>
          <p className="mt-3 text-sm font-light text-muted-foreground">
            View discovery call enquiries received through the site.
          </p>
          <form data-testid="admin-login-form" onSubmit={login} className="mt-10 flex flex-col gap-8">
            <div>
              <label htmlFor="admin-email" className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted-foreground">
                Email
              </label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="luxury-input"
                placeholder="admin@ayurenew.com"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="text-[10px] font-medium uppercase tracking-[0.3em] text-muted-foreground">
                Password
              </label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="luxury-input"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm font-light text-red-700">
                {error}
              </p>
            )}
            <LuxuryButton data-testid="admin-login-submit" type="submit" disabled={loading}>
              {loading ? "Signing in…" : "Sign In"}
            </LuxuryButton>
          </form>
          <Link
            to="/"
            data-testid="admin-back-link"
            className="mt-10 inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.3em] text-muted-foreground transition-colors duration-300 hover:text-accent"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="mx-auto min-h-screen max-w-[1200px] px-6 py-16 lg:px-12">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <Logo />
          <h1 className="mt-8 font-serif text-3xl font-light sm:text-4xl">
            Discovery call <em className="text-accent">enquiries</em>
          </h1>
          <p data-testid="admin-enquiry-count" className="mt-2 text-sm font-light text-muted-foreground">
            {enquiries ? `${enquiries.length} enquir${enquiries.length === 1 ? "y" : "ies"} received` : "Loading…"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LuxuryButton data-testid="admin-refresh-button" variant="ghost" className="px-5 py-3" onClick={() => load(token)}>
            <RefreshCw size={13} /> Refresh
          </LuxuryButton>
          <LuxuryButton data-testid="admin-logout-button" variant="ghost" className="px-5 py-3" onClick={logout}>
            <LogOut size={13} /> Sign Out
          </LuxuryButton>
        </div>
      </div>

      <div className="mt-12 border-t border-border/60">
        {enquiries && enquiries.length === 0 && (
          <p data-testid="admin-empty-state" className="py-16 text-center font-serif text-2xl font-light italic text-muted-foreground">
            No enquiries yet — they will appear here as they arrive.
          </p>
        )}
        {enquiries &&
          enquiries.map((enq) => (
            <article
              key={enq.id}
              data-testid={`admin-enquiry-${enq.id}`}
              className="grid gap-4 border-b border-border/60 py-8 lg:grid-cols-12"
            >
              <div className="lg:col-span-3">
                <p className="font-serif text-xl">{enq.name}</p>
                <p className="mt-1 text-sm font-light text-accent">{enq.email}</p>
                {enq.phone && <p className="mt-1 text-sm font-light text-muted-foreground">{enq.phone}</p>}
              </div>
              <div className="lg:col-span-3">
                <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">Programme</p>
                <p className="mt-2 text-sm font-light text-foreground/85">{enq.programme || "Not specified"}</p>
                <p className="mt-3 text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">Received</p>
                <p className="mt-1 text-sm font-light text-muted-foreground">
                  {new Date(enq.created_at).toLocaleString()}
                </p>
              </div>
              <p className="text-sm font-light leading-relaxed text-foreground/80 lg:col-span-6">{enq.message}</p>
            </article>
          ))}
      </div>
    </main>
  );
}
